import React, {useCallback, useEffect, useState} from 'react';
import {useSelector, useDispatch, useStore} from 'react-redux';
import Iframe from 'react-iframe';
import {disableWebboard} from 'store/media'; // Import the action to clear webboard
import {openPlansPage} from 'utils/subscriptionPlans';
import {
  setCourseData,
  setCurrentLessonProgress,
  fetchCourseData,
  setShowUnitSelectionScreen,
} from '../../store/course';
import {
  API_KEY,
  QUIKLEARN_BACKEND_URL,
  TOKEN_GENERATION_API,
} from 'config/app.config';
import {getQuikcoinsBalance} from 'utils/quikcoinsService';
import {buildWebLaunchLocation, getWebAccessToken} from 'utils/webAuthSession';

const parseRecommendedUnitNumber = recommendedUnit => {
  if (!recommendedUnit) return null;
  const s = String(recommendedUnit);
  const m = /unit\s*(\d+)/i.exec(s);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const WebboardService = () => {
  const webboardUrl = useSelector(state => state.media.webboardUrl);
  const courseData = useSelector(state => state.course.courseData);
  const curriculumList = useSelector(state => state.course.curriculumList);
  const curriculumGS = useSelector(state => state.course.curriculumGradeAndSubject);
  const worldToLoad = useSelector(state => state.app?.courseTokenValues?.worldToLoad);
  const tokenUnitId = useSelector(state => state.app?.courseTokenValues?.unitId);
  const customCourse = useSelector(state => state.meeting?.meeting?.customCourse);
  const userId = useSelector(state => state.app?.courseTokenValues?.userId);
  const token = useSelector(state => state.meeting?.properties?.token);
  const avatarUrl = useSelector(state => state.meeting?.meeting?.avatarUrlValue);
  const dispatch = useDispatch();
  const store = useStore();
  const [showWebboard, setShowWebboard] = useState(false);
  const [pendingQuizCompletion, setPendingQuizCompletion] = useState(null);
  const refreshTimerRef = React.useRef(null);

  // Always show the iframe when a new webboard URL is received
  useEffect(() => {
    if (webboardUrl) {
      setShowWebboard(true);
    }
  }, [webboardUrl]);

  // Hide and clear the webboard when the close button is clicked
  const hideWebboard = useCallback(() => {
    setShowWebboard(false);
    dispatch(disableWebboard()); // Clear the webboard URL from Redux state
  }, [dispatch]);

  const emitDetailsRefreshEvent = useCallback((phase, detail = {}) => {
    try {
      window.dispatchEvent(
        new CustomEvent(`course:detailsRefresh:${phase}`, {
          detail,
        }),
      );
    } catch (_) {}
  }, []);

  const refreshCourseDetailsBestEffort = useCallback(async (reason = 'Assessment', meta = {}) => {
    try {
      const curriculumId = courseData?.curriculumId;
      const grade = curriculumGS?.grade;
      const subject = curriculumGS?.subject;
      const resolvedWorldToLoad = worldToLoad;
      const resolvedUnitId =
        tokenUnitId ||
        courseData?.units?.[courseData?.currentUnitIndex]?.id ||
        courseData?.units?.[0]?.id ||
        null;

      if (!token) return;
      if (!curriculumId) return;
      if (!resolvedWorldToLoad) return;

      emitDetailsRefreshEvent('start', { reason, ...meta });

      // Refetch /details after assessment closes so masteryScore/masteryStatus
      // are guaranteed to be correct everywhere (unit cards, lesson overlays, etc.).
      await dispatch(
        fetchCourseData({
          currentCurriculumId: curriculumId,
          currentUnitId: resolvedUnitId,
          token,
          worldToLoad: resolvedWorldToLoad,
          curriculumList: curriculumList ?? null,
          isTokenBasedFetching: true,
          grade,
          subject,
          customCourse: Boolean(customCourse),
        }),
      );
      emitDetailsRefreshEvent('end', { reason, ...meta, success: true });
    } catch (_) {
      // Best-effort only: do not block UI close if refresh fails.
      emitDetailsRefreshEvent('end', { reason, ...meta, success: false });
    }
  }, [courseData, curriculumGS, worldToLoad, tokenUnitId, token, dispatch, curriculumList, customCourse, emitDetailsRefreshEvent]);

  const scheduleDetailsRefresh = useCallback((reason, meta = {}) => {
    try {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      // Debounce rapid question submissions / score pings.
      refreshTimerRef.current = setTimeout(() => {
        refreshCourseDetailsBestEffort(reason, meta);
      }, 600);
    } catch (_) {}
  }, [refreshCourseDetailsBestEffort]);

  const syncCoinsBestEffort = useCallback(async (reason = 'Assessment') => {
    try {
      if (!userId) return;
      const response = await getQuikcoinsBalance({userId, token});
      const balance =
        response?.data?.balance ??
        response?.balance ??
        response?.data?.newBalance ??
        response?.newBalance;
      const numeric = Number(balance);
      if (!Number.isFinite(numeric)) return;

      // Best-effort: store + emit an event (Meeting can listen if desired)
      try {
        window.localStorage?.setItem('quikcoinsBalance', String(numeric));
      } catch (_) {}
      try {
        window.dispatchEvent(
          new CustomEvent('quikcoins:balance', {
            detail: {balance: numeric, reason},
          }),
        );
      } catch (_) {}
    } catch (_) {}
  }, [token, userId]);

  const generateMetaverseToken = async ({curriculumId, unitId}) => {
    const base = String(QUIKLEARN_BACKEND_URL || '').replace(/\/$/, '');
    const endpoint = String(TOKEN_GENERATION_API || '');
    const url = `${base}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        Authorization: `Bearer ${getWebAccessToken() || ''}`,
      },
      body: JSON.stringify({
        userId,
        curriculumId,
        unitId,
      }),
    });
    if (!response.ok) throw new Error('Failed to generate metaverse token');
    const data = await response.json();
    return data?.token;
  };

  const launchRecommendedUnitFromQuizCompletionWeb = async ({
    mode,
    recommendedUnit,
    course = courseData,
  } = {}) => {
    if (mode !== 'diagnostic') return;
    if (!userId) throw new Error('Missing userId');
    const unitNumber = parseRecommendedUnitNumber(recommendedUnit);
    if (!unitNumber) throw new Error('Invalid recommendedUnit');

    const units = course?.units || [];
    const unit =
      units.find(u => Number(u.unitNumber) === Number(unitNumber)) ||
      units[unitNumber - 1] ||
      null;
    if (!course?.curriculumId) throw new Error('Missing curriculumId');
    if (!unit?.id) throw new Error('Could not resolve unit from recommendedUnit');

    // [DIAG] final resolution used for the metaverse redirect
    console.log('[DIAG launch] resolved:', {
      recommendedUnit,
      unitNumber,
      resolvedUnitId: unit.id,
      resolvedUnitNumber: unit.unitNumber,
      resolvedUnitTitle: unit.title,
      curriculumId: course.curriculumId,
    });

    // Preset first lesson context (mirrors UnitSelectionOverlay)
    try {
      const firstLesson = unit?.lessons?.[0];
      if (firstLesson) {
        dispatch(
          setCourseData({
            ...course,
            currentLessonId: firstLesson.lessonId,
            currentLessonTotalSlides: firstLesson.totalSlides,
          }),
        );
        dispatch(
          setCurrentLessonProgress({
            currentLessonId: firstLesson.lessonId,
            currentLessonProgressPercentage: Number(firstLesson.progress) || 0,
            currentLessonTotalSlides: Number(firstLesson.totalSlides) || 0,
            currentLessonMasteryScore: Number(firstLesson.masteryScore) || 0,
          }),
        );
      }
    } catch (_) {}

    const metaverseToken = await generateMetaverseToken({
      curriculumId: course.curriculumId,
      unitId: unit.id,
    });
    if (!metaverseToken) throw new Error('No token returned');

    window.location.href = buildWebLaunchLocation(window.location.origin, metaverseToken, {
      avatarSelection: false,
      legacy: false,
      avatarUrl: String(avatarUrl || ''),
    });
  };

  // If quizCompletion arrives before courseData is loaded, we stash it and retry
  // once courseData becomes available.
  useEffect(() => {
    if (!pendingQuizCompletion) return;
    if (!courseData) return;

    (async () => {
      try {
        await launchRecommendedUnitFromQuizCompletionWeb(pendingQuizCompletion);
      } catch (e) {
        // If we can't launch, fall back to unit selection so the user can proceed.
        try {
          dispatch(setShowUnitSelectionScreen(true));
        } catch (_) {}
      } finally {
        setPendingQuizCompletion(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseData, pendingQuizCompletion]);

  const handleLessonMasteryAchieved = data => {
    try {
      if (!courseData || !courseData.units || !data?.unitId || !data?.lessonId) {
        return;
      }

      const updatedUnits = (courseData.units || []).map(unit => {
        if (unit.id !== data.unitId) return unit;

        const updatedLessons = (unit.lessons || []).map(lesson => {
          if (lesson.lessonId !== data.lessonId) return lesson;

          const existingProgress =
            typeof lesson.progress === 'number'
              ? lesson.progress
              : typeof lesson.progressValue === 'number'
                ? lesson.progressValue
                : 0;

          return {
            ...lesson,
            // Do NOT force lesson progress to 100 here; it should reflect actual
            // video/slide completion. Only mark mastery status.
            progress: existingProgress,
            isMastered: true,
            masteryStatus: 'MASTERED',
          };
        });

        const totalLessons = updatedLessons.length;
        const lessonsMastered = updatedLessons.filter(l => l.isMastered).length;
        const unitMasteryPercentage =
          totalLessons > 0 ? Math.round((lessonsMastered / totalLessons) * 100) : 0;
        const lessonsCompletedPercentage =
          totalLessons > 0
            ? Math.round(
                updatedLessons.reduce((acc, l) => acc + (l.progress || 0), 0) /
                  totalLessons,
              )
            : 0;
        const hundredPercentCompletedCount = updatedLessons.filter(
          l => l.progress === 100,
        ).length;

        return {
          ...unit,
          lessons: updatedLessons,
          totalLessons,
          lessonsMastered,
          unitMasteryPercentage,
          lessonsCompletedPercentage,
          hundredPercentCompletedCount,
        };
      });

      const totalUnits = updatedUnits.length;
      const LESSON_MASTERY_THRESHOLD = 90;
      const unitsMastered = updatedUnits.filter(
        u => (u.unitMasteryPercentage ?? 0) >= LESSON_MASTERY_THRESHOLD,
      ).length;
      const calculatedCourseMastery =
        totalUnits > 0 ? Math.round((unitsMastered / totalUnits) * 100) : 0;

      const updatedCourseData = {
        ...courseData,
        units: updatedUnits,
        courseMastery: calculatedCourseMastery,
      };

      dispatch(setCourseData(updatedCourseData));
    } catch (_) {}
  };

  useEffect(() => {
    const handleMessage = event => {
      if (!event?.data) return;

      // Log every event we receive

      // webboard sometimes posts objects, sometimes JSON strings
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (_) {
          // keep as-is
        }
      }

      if (data && data.type) {
        // Helpful debug log (can be removed later)
        console.log('[WebboardService] Received event:', data);
        if (data.type === 'updatedLessonMasteryScore') {
          // `data.score` is not guaranteed to be the same unit as our backend `masteryScore`.
          // Some assessment builds send a percentage here. To avoid UI jumping to 100%,
          // treat `/details` as source of truth and refresh it (debounced).
          const lessonId = data.lessonId || data.lesson_id || data.lesson || null;
          scheduleDetailsRefresh('Lesson Mastery Update', { lessonId });
          syncCoinsBestEffort('Lesson Mastery');
        } else if (data.type === 'openPlans') {
          // The placement test on a plan without it: "See plans" (Sol owner-retest2 Q4 A).
          dispatch(disableWebboard());
          openPlansPage();
        } else if (data.type === 'quizCompletion') {
          (async () => {
            try {
              // The recommended unit number is relative to the curriculum the
              // placement test was taken for — identified by grade + subject.
              // Resolve the real course by grade+subject (the bundled diagnostic
              // ids come from a different data source and can't be resolved),
              // then pick the unit by position.
              const targetGrade = data.grade ?? curriculumGS?.grade ?? null;
              const targetSubject = data.subject ?? curriculumGS?.subject ?? null;
              const gsMatches = gs =>
                gs &&
                String(gs.grade) === String(targetGrade) &&
                String(gs.subject || '').toLowerCase() ===
                  String(targetSubject || '').toLowerCase();

              let course = courseData;

              console.log('[DIAG quizCompletion] message:', {
                mode: data.mode,
                recommendedUnit: data.recommendedUnit,
                gradeInMessage: data.grade,
                subjectInMessage: data.subject,
                targetGrade,
                targetSubject,
                loadedGradeSubject: curriculumGS,
              });

              // [DIAG] what does the runtime curriculum list actually contain,
              // and does grade+subject resolve locally?
              try {
                const list = curriculumList || [];
                const localMatch = list.find(
                  c =>
                    String(c.grade) === String(targetGrade) &&
                    String(c.subject || '').toLowerCase() ===
                      String(targetSubject || '').toLowerCase(),
                );
                console.log('[DIAG quizCompletion] curriculumList probe:', {
                  listLen: list.length,
                  gradesPresent: [...new Set(list.map(c => String(c.grade)))],
                  subjectsPresent: [...new Set(list.map(c => String(c.subject)))],
                  localMatchId: localMatch?.id ?? null,
                });
              } catch (_) {}

              if (!(course && gsMatches(curriculumGS)) && targetGrade && targetSubject && token) {
                console.log('[DIAG quizCompletion] fetching by grade+subject', targetGrade, targetSubject);
                await dispatch(
                  fetchCourseData({
                    token,
                    worldToLoad: 'quikbase',
                    // Force a fresh backend list fetch so a stale/partial Redux
                    // curriculumList can't cause the grade+subject lookup to miss.
                    curriculumList: null,
                    isTokenBasedFetching: false,
                    grade: targetGrade,
                    subject: targetSubject,
                    customCourse: false,
                  }),
                );
                // fetchCourseData swallows failures (leaving the previous course
                // in place), so confirm it actually landed on the target
                // grade+subject before resolving against it.
                const nowGS = store.getState().course.curriculumGradeAndSubject;
                console.log('[DIAG quizCompletion] after fetch:', {
                  fetchedGradeSubject: nowGS,
                  fetchedCurriculumId: store.getState().course.courseData?.curriculumId,
                  matches: gsMatches(nowGS),
                });
                if (!gsMatches(nowGS)) {
                  throw new Error('Placement test course failed to load');
                }
                course = store.getState().course.courseData;
              }

              await launchRecommendedUnitFromQuizCompletionWeb({
                mode: data.mode,
                recommendedUnit: data.recommendedUnit,
                course,
              });
            } catch (e) {
              console.log('[DIAG quizCompletion] FELL BACK to unit selection:', e?.message || e);
              // If we can't launch, fall back to unit selection so the user can proceed.
              try {
                dispatch(setShowUnitSelectionScreen(true));
              } catch (_) {}
            }
          })();
        } else if (data.type === 'assessmentQuestionSubmitted') {
          // Debounced refresh so lesson mastery % stays correct while assessment is open.
          const lessonId = data.lessonId || data.lesson_id || data.lesson || null;
          scheduleDetailsRefresh('Assessment Question', { lessonId });
        } else if (data.type === 'assessmentSessionSummary') {
          // Web-only branch: message recognized (analytics handled elsewhere / not wired here).
        } else if (data.type === 'lessonMasteryAchieved') {
          handleLessonMasteryAchieved(data);
        } else if (data.type === 'disableWebboard') {
          hideWebboard();
          syncCoinsBestEffort('Assessment');
          refreshCourseDetailsBestEffort('Assessment');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      try {
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      } catch (_) {}
    };
  }, [
    avatarUrl,
    courseData,
    curriculumGS?.grade,
    curriculumGS?.subject,
    curriculumList,
    dispatch,
    fetchCourseData,
    hideWebboard,
    pendingQuizCompletion,
    refreshCourseDetailsBestEffort,
    scheduleDetailsRefresh,
    syncCoinsBestEffort,
    token,
    userId,
  ]);

  return (
    <>
      {showWebboard && webboardUrl && (
        <div
          style={{
            position: 'absolute',
            top: '0px',
            left: '0px',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 1)',
            zIndex: 100,
          }}>
          {/* Close Button in Top Right */}
          <button
            onClick={hideWebboard}
            style={{
              position: 'absolute',
              top: '20px',
              right: '10px',
              width: '40px',
              height: '40px',
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'black',
              backgroundColor: '#C5E836',
              borderRadius: 10,
              cursor: 'pointer',
            }}>
            ✖
          </button>

          {/* Embedded Webboard */}
          <Iframe
            url={webboardUrl}
            width="100%"
            height="100%"
            id="webboard-iframe"
            style={{position: 'relative', border: 'none'}}
          />
        </div>
      )}
    </>
  );
};
